import { useEffect, useState, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { NotificationsProvider } from "@/context/NotificationsContext";
import { I18nProvider } from "@/context/I18nContext";
import { VisitorProvider } from "@/context/VisitorContext";
import { CartProvider } from "@/context/CartContext";
import { VisitorAuthModal } from "@/components/VisitorAuthModal";
import { SUPPORTED } from "@/i18n/translations";
import Layout from "@/components/Layout";

import Landing from "@/pages/Landing";
import { PAGE_KEYS, PAGE_SLUGS } from "@/i18n/pages";
import { SPECIES_KEYS, SPECIES_SLUGS } from "@/i18n/breeding";
import { USECASE_KEYS, USECASE_SLUGS } from "@/i18n/usecases";
import { EGGCALC_SLUGS } from "@/i18n/eggcalc";
import { HENPROFIT_SLUGS } from "@/i18n/henprofit";
import { HATCH_SLUGS } from "@/i18n/hatch";
import { FEED_SLUGS } from "@/i18n/feed";
import { COOP_SLUGS } from "@/i18n/coop";
import { DIRECTORY_SLUGS } from "@/i18n/directory";
import { SECTION_KEYS, sectionSlug } from "@/i18n/section_slugs";

// Route components are code-split (React.lazy): the landing page ships a much smaller
// initial JS bundle — dashboard/admin/charts/maps/blog load only when their route is opened.
const Forum = lazy(() => import("@/pages/Forum"));
const MarketingPage = lazy(() => import("@/pages/MarketingPage"));
const BreedingPage = lazy(() => import("@/pages/BreedingPage"));
const UseCasePage = lazy(() => import("@/pages/UseCasePage"));
const EggCostCalculator = lazy(() => import("@/pages/EggCostCalculator"));
const HenProfitCalculator = lazy(() => import("@/pages/HenProfitCalculator"));
const HatchDateCalculator = lazy(() => import("@/pages/HatchDateCalculator"));
const FeedCalculator = lazy(() => import("@/pages/FeedCalculator"));
const CoopSizeCalculator = lazy(() => import("@/pages/CoopSizeCalculator"));
const EmbedCalculator = lazy(() => import("@/pages/EmbedCalculator"));
const BadgeEmbed = lazy(() => import("@/pages/BadgeEmbed"));
const ToolsPage = lazy(() => import("@/pages/ToolsPage"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const DemoPage = lazy(() => import("@/pages/DemoPage"));
const Auth = lazy(() => import("@/pages/Auth"));
const Parrain = lazy(() => import("@/pages/Parrain"));
const Sante = lazy(() => import("@/pages/Sante"));
const Avis = lazy(() => import("@/pages/Avis"));
const Onboarding = lazy(() => import("@/pages/Onboarding"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Elevage = lazy(() => import("@/pages/Elevage"));
const Oeufs = lazy(() => import("@/pages/Oeufs"));
const Production = lazy(() => import("@/pages/Production"));
const Reproduction = lazy(() => import("@/pages/Reproduction"));
const Poulaillers = lazy(() => import("@/pages/Poulaillers"));
const Produits = lazy(() => import("@/pages/Produits"));
const Stock = lazy(() => import("@/pages/Stock"));
const Commandes = lazy(() => import("@/pages/Commandes"));
const Clients = lazy(() => import("@/pages/Clients"));
const ImportData = lazy(() => import("@/pages/ImportData"));
const Factures = lazy(() => import("@/pages/Factures"));
const Fournisseurs = lazy(() => import("@/pages/Fournisseurs"));
const Depenses = lazy(() => import("@/pages/Depenses"));
const Comptabilite = lazy(() => import("@/pages/Comptabilite"));
const Livraisons = lazy(() => import("@/pages/Livraisons"));
const Boutique = lazy(() => import("@/pages/Boutique"));
const Statistiques = lazy(() => import("@/pages/Statistiques"));
const Abonnement = lazy(() => import("@/pages/Abonnement"));
const Support = lazy(() => import("@/pages/Support"));
const Parametres = lazy(() => import("@/pages/Parametres"));
const Notifications = lazy(() => import("@/pages/Notifications"));
const PublicShop = lazy(() => import("@/pages/PublicShop"));
const ProductPage = lazy(() => import("@/pages/ProductPage"));
const Annuaire = lazy(() => import("@/pages/Annuaire"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const AuthorPage = lazy(() => import("@/pages/AuthorPage"));
const BlogSection = lazy(() => import("@/pages/BlogSection"));
const CGU = lazy(() => import("@/pages/legal/CGU"));
const Confidentialite = lazy(() => import("@/pages/legal/Confidentialite"));
const Cookies = lazy(() => import("@/pages/legal/Cookies"));
const Contact = lazy(() => import("@/pages/legal/Contact"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const Panier = lazy(() => import("@/pages/Panier"));
const SuperAdmin = lazy(() => import("@/pages/SuperAdmin"));
const DirectoryCategory = lazy(() => import("@/pages/DirectoryCategory"));
const Favoris = lazy(() => import("@/pages/Favoris"));

function Loader() {
  return <div className="min-h-screen grid place-items-center text-muted-foreground">Chargement…</div>;
}

function Protected({ children, titleKey }) {
  const { user, farm, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "superadmin") return <Navigate to="/admin" replace />;
  if (!farm) return <Navigate to="/onboarding" replace />;
  return <Layout titleKey={titleKey}>{children}</Layout>;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user || user.role !== "superadmin") return <Navigate to="/login" replace />;
  return children;
}

function OnboardingRoute() {
  const { user, farm, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (farm) return <Navigate to="/app" replace />;
  return <Onboarding />;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);
  return null;
}

function AppRoutes() {
  return (
    <Suspense fallback={<Loader />}>
    <Routes>
      <Route path="/" element={<Landing />} />
      {SUPPORTED.map((l) => <Route key={l} path={`/${l}`} element={<Landing />} />)}
      {SUPPORTED.map((l) => <Route key={l + "-blog"} path={`/${l}/blog`} element={<Blog />} />)}
      {SUPPORTED.map((l) => <Route key={l + "-bp"} path={`/${l}/blog/:slug`} element={<BlogPost />} />)}
      {SUPPORTED.map((l) => <Route key={l + "-auth"} path={`/${l}/auteur/:slug`} element={<AuthorPage />} />)}
      {SUPPORTED.map((l) => SECTION_KEYS.map((k) => <Route key={`${l}-sec-${k}`} path={`/${l}/${sectionSlug(l, k)}`} element={<BlogSection section={k} />} />))}
      <Route path="/fermes" element={<Annuaire />} />
      <Route path="/mes-favoris" element={<Favoris />} />
      <Route path="/fermes/:catSlug" element={<DirectoryCategory />} />
      {SUPPORTED.map((l) => <Route key={l + "-dir"} path={`/${l}/${DIRECTORY_SLUGS[l] || "farms"}`} element={<Annuaire />} />)}
      {SUPPORTED.filter((l) => l !== "fr").map((l) => <Route key={l + "-dircat"} path={`/${l}/${DIRECTORY_SLUGS[l] || "farms"}/:catSlug`} element={<DirectoryCategory />} />)}
      {PAGE_KEYS.flatMap((key) => SUPPORTED.map((l) => {
        const slug = PAGE_SLUGS[key][l];
        const path = l === "fr" ? `/${slug}` : `/${l}/${slug}`;
        return <Route key={`${key}-${l}`} path={path} element={<MarketingPage pageKey={key} lang={l} />} />;
      }))}
      {SPECIES_KEYS.flatMap((key) => SUPPORTED.map((l) => {
        const slug = SPECIES_SLUGS[key] && SPECIES_SLUGS[key][l];
        if (!slug) return null;
        const path = l === "fr" ? `/${slug}` : `/${l}/${slug}`;
        return <Route key={`sp-${key}-${l}`} path={path} element={<BreedingPage speciesKey={key} lang={l} />} />;
      }))}
      {USECASE_KEYS.flatMap((key) => SUPPORTED.map((l) => {
        const slug = USECASE_SLUGS[key] && USECASE_SLUGS[key][l];
        if (!slug) return null;
        const path = l === "fr" ? `/${slug}` : `/${l}/${slug}`;
        return <Route key={`uc-${key}-${l}`} path={path} element={<UseCasePage useKey={key} lang={l} />} />;
      }))}
      {SUPPORTED.map((l) => {
        const slug = EGGCALC_SLUGS[l];
        if (!slug) return null;
        const path = l === "fr" ? `/${slug}` : `/${l}/${slug}`;
        return <Route key={`egg-${l}`} path={path} element={<EggCostCalculator lang={l} />} />;
      })}
      {SUPPORTED.map((l) => {
        const slug = HENPROFIT_SLUGS[l];
        if (!slug) return null;
        const path = l === "fr" ? `/${slug}` : `/${l}/${slug}`;
        return <Route key={`hp-${l}`} path={path} element={<HenProfitCalculator lang={l} />} />;
      })}
      {SUPPORTED.map((l) => {
        const slug = HATCH_SLUGS[l];
        if (!slug) return null;
        const path = l === "fr" ? `/${slug}` : `/${l}/${slug}`;
        return <Route key={`hatch-${l}`} path={path} element={<HatchDateCalculator lang={l} />} />;
      })}
      {SUPPORTED.map((l) => {
        const slug = FEED_SLUGS[l];
        if (!slug) return null;
        const path = l === "fr" ? `/${slug}` : `/${l}/${slug}`;
        return <Route key={`feed-${l}`} path={path} element={<FeedCalculator lang={l} />} />;
      })}
      {SUPPORTED.map((l) => {
        const slug = COOP_SLUGS[l];
        if (!slug) return null;
        const path = l === "fr" ? `/${slug}` : `/${l}/${slug}`;
        return <Route key={`coop-${l}`} path={path} element={<CoopSizeCalculator lang={l} />} />;
      })}
      <Route path="/embed/:tool" element={<EmbedCalculator />} />
      <Route path="/embed/badge/:slug" element={<BadgeEmbed />} />
      {SUPPORTED.map((l) => (
        <Route key={`tools-${l}`} path={l === "fr" ? "/outils" : `/${l}/outils`} element={<ToolsPage lang={l} />} />
      ))}
      <Route path="/demo" element={<DemoPage />} />
      <Route path="/forum" element={<Forum />} />
      <Route path="/forum/membre/:pseudo" element={<Forum />} />
      <Route path="/forum/:category" element={<Forum />} />
      <Route path="/forum/:category/:slug" element={<Forum />} />
      {SUPPORTED.filter((l) => l !== "fr").map((l) => <Route key={l + "-forum"} path={`/${l}/forum`} element={<Forum />} />)}
      {SUPPORTED.filter((l) => l !== "fr").map((l) => <Route key={l + "-forum-m"} path={`/${l}/forum/membre/:pseudo`} element={<Forum />} />)}
      {SUPPORTED.filter((l) => l !== "fr").map((l) => <Route key={l + "-forum-c"} path={`/${l}/forum/:category`} element={<Forum />} />)}
      {SUPPORTED.filter((l) => l !== "fr").map((l) => <Route key={l + "-forum-t"} path={`/${l}/forum/:category/:slug`} element={<Forum />} />)}
      <Route path="/login" element={<Auth mode="login" />} />
      <Route path="/register" element={<Auth mode="register" />} />
      <Route path="/parrain/:code" element={<Parrain />} />
      <Route path="/onboarding" element={<OnboardingRoute />} />
      <Route path="/boutique/:slug" element={<PublicShop />} />
      <Route path="/boutique/:slug/:productSlug" element={<ProductPage />} />
      <Route path="/panier" element={<Panier />} />
      {SUPPORTED.filter((l) => l !== "fr").map((l) => <Route key={l + "-pdp"} path={`/${l}/boutique/:slug/:productSlug`} element={<ProductPage />} />)}
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/auteur/:slug" element={<AuthorPage />} />
      <Route path="/actualites" element={<BlogSection section="news" />} />
      <Route path="/conseils" element={<BlogSection section="advice" />} />
      <Route path="/histoires-de-fermes" element={<BlogSection section="stories" />} />
      <Route path="/cgu" element={<CGU />} />
      <Route path="/confidentialite" element={<Confidentialite />} />
      <Route path="/cookies" element={<Cookies />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentSuccess />} />
      <Route path="/admin" element={<AdminRoute><SuperAdmin /></AdminRoute>} />

      <Route path="/app" element={<Protected titleKey="home"><Dashboard /></Protected>} />
      <Route path="/app/elevage" element={<Protected titleKey="elevage"><Elevage /></Protected>} />
      <Route path="/app/sante" element={<Protected titleKey="health"><Sante /></Protected>} />
      <Route path="/app/avis" element={<Protected titleKey="reviews"><Avis /></Protected>} />
      <Route path="/app/oeufs" element={<Protected titleKey="eggs"><Oeufs /></Protected>} />
      <Route path="/app/production" element={<Protected titleKey="production"><Production /></Protected>} />
      <Route path="/app/reproduction" element={<Protected titleKey="reproduction"><Reproduction /></Protected>} />
      <Route path="/app/poulaillers" element={<Protected titleKey="houses"><Poulaillers /></Protected>} />
      <Route path="/app/produits" element={<Protected titleKey="products"><Produits /></Protected>} />
      <Route path="/app/stock" element={<Protected titleKey="stock"><Stock /></Protected>} />
      <Route path="/app/commandes" element={<Protected titleKey="orders"><Commandes /></Protected>} />
      <Route path="/app/factures" element={<Protected titleKey="invoices"><Factures /></Protected>} />
      <Route path="/app/clients" element={<Protected titleKey="customers"><Clients /></Protected>} />
      <Route path="/app/import" element={<Protected titleKey="import"><ImportData /></Protected>} />
      <Route path="/app/fournisseurs" element={<Protected titleKey="suppliers"><Fournisseurs /></Protected>} />
      <Route path="/app/depenses" element={<Protected titleKey="expenses"><Depenses /></Protected>} />
      <Route path="/app/comptabilite" element={<Protected titleKey="accounting"><Comptabilite /></Protected>} />
      <Route path="/app/livraisons" element={<Protected titleKey="deliveries"><Livraisons /></Protected>} />
      <Route path="/app/boutique" element={<Protected titleKey="shop"><Boutique /></Protected>} />
      <Route path="/app/statistiques" element={<Protected titleKey="stats"><Statistiques /></Protected>} />
      <Route path="/app/abonnement" element={<Protected titleKey="subscription"><Abonnement /></Protected>} />
      <Route path="/app/support" element={<Protected titleKey="support"><Support /></Protected>} />
      <Route path="/app/parametres" element={<Protected titleKey="settings"><Parametres /></Protected>} />
      <Route path="/app/notifications" element={<Protected titleKey="notifications"><Notifications /></Protected>} />
      <Route path="*" element={<RedirectCatcher />} />
    </Routes>
    </Suspense>
  );
}

function RedirectCatcher() {
  const loc = useLocation();
  const [state, setState] = useState("loading"); // loading | notfound
  useEffect(() => {
    let active = true;
    setState("loading");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seo/redirect?path=${encodeURIComponent(loc.pathname)}`)
      .then((r) => r.json())
      .then((d) => { if (!active) return; if (d && d.to) window.location.replace(d.to); else setState("notfound"); })
      .catch(() => { if (active) setState("notfound"); });
    return () => { active = false; };
  }, [loc.pathname]); // eslint-disable-line
  if (state === "notfound") return <NotFound />;
  return null;
}

export default function App() {
  return (
    <div className="App">
      <I18nProvider>
        <AuthProvider>
          <VisitorProvider>
            <CartProvider>
              <NotificationsProvider>
                <BrowserRouter>
                  <ScrollToTop />
                  <AppRoutes />
                  <VisitorAuthModal />
                  <Toaster position="top-center" richColors />
                </BrowserRouter>
              </NotificationsProvider>
            </CartProvider>
          </VisitorProvider>
        </AuthProvider>
      </I18nProvider>
    </div>
  );
}
